import { Button, Flex, Modal, Table, TableProps, Form } from 'antd';
import { useState, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

// components
import ListButton from 'components/listButton/listButton';
import StatusComponent from 'components/status/status';
import NoData from 'components/noData/noData';
import MemberForm from './memberForm';

// services
import { isRTL } from 'core/services/language';
import { StatusColors } from 'utils/statusColors';
import { mapResponseErrors } from 'utils/mapResponseErrors';
import NotificationService from 'core/services/notification';
import { onUpdateCommitteeSecretary } from '../services/memberServices';

// models
import { CommitteeStatus } from '../models/committee';
import { CommitteeMember } from '../models/members';

// hooks
import useCompanySuspended from 'core/hooks/useCompanySuspended';

// components
import SuspendedTooltip from 'components/suspendedTooltip/suspendedTooltip';

interface CommitteeMembersProps {
	committeeStatus: CommitteeStatus;
	members: CommitteeMember[];
	viewOnly?: boolean;
	canUpdateSecretary?: boolean;
	committeeId?: number;
	isLoading?: boolean;
	onEdit?(member: CommitteeMember): void;
	onDelete?(memberId: number): void;
	refresh?(): void;
}

const CommitteeMembers: FunctionComponent<CommitteeMembersProps> = ({
	committeeStatus,
	members = [],
	viewOnly,
	canUpdateSecretary,
	committeeId,
	isLoading,
	onEdit,
	onDelete,
	refresh,
}) => {
	const { t } = useTranslation();

	const { suspendedProps } = useCompanySuspended();

	const [viewForm] = Form.useForm();
	const [updateSecretaryForm] = Form.useForm();

	const [viewModalOpen, setViewModalOpen] = useState(false);
	const [updateSecretaryModalOpen, setUpdateSecretaryModalOpen] = useState(false);

	const [tempSecretaryId, setTempSecretaryId] = useState<number | null>(null);
	const [isUpdatingSecretary, setIsUpdatingSecretary] = useState(false);

	const columns: TableProps<CommitteeMember>['columns'] = [
		{
			title: t('ACTIONS.MEMBER_NAME'),
			dataIndex: 'member_name',
			key: 'member_name',
			// render: (_, row) => (isRTL() ? row.name : row.name_en ?? row.name),
		},
		{
			title: t('COMMON.NATIONAL_ID'),
			dataIndex: 'national_id',
			key: 'national_id',
		},
		{
			title: t('ACTIONS.POSITION'),
			dataIndex: 'translated_position',
			key: 'translated_position',
			render: (translated_position) => translated_position[isRTL() ? 'ar' : 'en'],
		},
		{
			title: t('ACTIONS.TYPE'),
			dataIndex: 'translated_type',
			key: 'translated_type',
			render: (translated_type) => translated_type[isRTL() ? 'ar' : 'en'],
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status) => <StatusComponent text={t(`COMMON.${status?.toUpperCase()}`)} color={StatusColors[status]} />,
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '_',
			key: '_',
			render: (_, row) => (
				<Flex justify="center" gap={10}>
					<ListButton
						type="view"
						text={t('COMMON.VIEW')}
						onClick={() => {
							viewForm.setFieldsValue(row);
							setViewModalOpen(true);
						}}
					/>

					{viewOnly && canUpdateSecretary && row.status === 'active' && row.position === 'Committee-Secretary' && committeeStatus === 'active' && (
						<ListButton
							type="edit"
							{...suspendedProps}
							text={t('ACTIONS.UPDATE_SECRETARY')}
							onClick={() => {
								updateSecretaryForm.resetFields();
								setTempSecretaryId(row.id);
								setUpdateSecretaryModalOpen(true);
							}}
						/>
					)}

					{!viewOnly && committeeStatus && committeeStatus !== 'active' && (
						<>
							<ListButton
								type="edit"
								{...suspendedProps}
								onClick={() => {
									onEdit && onEdit(row);
								}}
							/>
							<ListButton
								type="delete"
								{...suspendedProps}
								onClick={() => {
									onDelete && onDelete(row.id);
								}}
							/>
						</>
					)}
				</Flex>
			),
		},
	];

	const updateSecretaryHandler = async (member: CommitteeMember) => {
		try {
			setIsUpdatingSecretary(true);

			const payload = new FormData();

			payload.append('current_secretary_id', tempSecretaryId!.toString());

			if (member.id) {
				payload.append('national_id', member.national_id);
				payload.append('position', member.position);
				payload.append('type', member.type);
			} else {
				Object.entries(member).forEach(([key, value]) => {
					payload.append(key, value as any);
				});
			}

			await onUpdateCommitteeSecretary(committeeId!, payload);

			setIsUpdatingSecretary(false);
			setUpdateSecretaryModalOpen(false);

			refresh?.();

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.SECRETARY_UPDATED_SUCCESSFULLY'));
		} catch (error) {
			setIsUpdatingSecretary(false);
			mapResponseErrors(error, updateSecretaryForm);
			NotificationService.error(t('COMMON.ERROR', t('ACTIONS.UPDATE_SECRETARY_ERROR')));
		}
	};

	return (
		<>
			<Table
				key="national_id"
				columns={columns}
				dataSource={members}
				className="bordered"
				scroll={{ x: 600 }}
				rowKey="id"
				loading={isLoading}
				locale={{ emptyText: <NoData /> }}
				pagination={false}
			/>

			{/* VIEW MODAL */}
			<Modal
				open={viewModalOpen}
				width={900}
				title={t('ACTIONS.MEMBER_DETAILS')}
				onCancel={() => setViewModalOpen(false)}
				footer={[
					<Button key="discard" type="link" danger size="middle" onClick={() => setViewModalOpen(false)}>
						{t('COMMON.CLOSE')}
					</Button>,
				]}>
				<MemberForm form={viewForm} showOnly />
			</Modal>

			{/* UPDATE SECRETARY MODAL */}
			<Modal
				open={updateSecretaryModalOpen}
				width={900}
				title={t('ACTIONS.MEMBER_DETAILS')}
				onCancel={() => setUpdateSecretaryModalOpen(false)}
				footer={[
					<Button key="discard" type="link" danger size="middle" onClick={() => setUpdateSecretaryModalOpen(false)}>
						{t('COMMON.CLOSE')}
					</Button>,
					<SuspendedTooltip key="confirm">
						<Button key="confirm" type="primary" size="middle" onClick={updateSecretaryForm.submit} loading={isUpdatingSecretary}>
							{t('COMMON.UPDATE')}
						</Button>
					</SuspendedTooltip>,
				]}>
				<MemberForm form={updateSecretaryForm} onSubmit={updateSecretaryHandler} secretaryOnly />
			</Modal>
		</>
	);
};

export default CommitteeMembers;
